/* eslint-disable @typescript-eslint/no-explicit-any */
// Location: src/components/admin/analytics/KarmaSocialRewardsAnalytics.tsx
// Purpose: KARMA Social Rewards analytics dashboard showing tipping and rewards
// Scope: Display tipping stats, leaderboards, and recent transactions

import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Wallet, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface KarmaSocialRewardsAnalyticsProps {
	className?: string;
}

export function KarmaSocialRewardsAnalytics({
	className,
}: KarmaSocialRewardsAnalyticsProps) {
	const { user, isAdmin } = useAuth();
	const navigate = useNavigate();

	// Query tipping analytics
	const userTipStats = useQuery(
		api.tipAnalytics.getUserTipStats,
		user ? { userId: user._id as unknown as Id<'users'> } : 'skip'
	);

	// Query recent tip transactions
	const recentTips = useQuery(
		api.tipAnalytics.getRecentTipTransactions,
		user
			? { userId: user._id as unknown as Id<'users'>, limit: 10 }
			: 'skip'
	);

	// Query tipping leaderboards
	const topTipReceivers = useQuery(api.tipAnalytics.getTippingLeaderboard, {
		type: 'received',
		limit: 5,
	});

	const topTipSenders = useQuery(api.tipAnalytics.getTippingLeaderboard, {
		type: 'sent',
		limit: 5,
	});

	if (!user || !isAdmin) {
		return (
			<div className='container mx-auto p-6'>
				<Card className='mt-6'>
					<CardContent>
						<div className='flex flex-col items-center justify-center space-y-6 p-8'>
							<h2 className='text-2xl font-bold text-red-500'>
								Access Denied
							</h2>
							<p className='text-lg text-gray-400 text-center'>
								You do not have permission to access KARMA
								Social Rewards Analytics.
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const formatNumber = (num: number | undefined) => {
		if (num === undefined || num === null) return '0';
		return num.toLocaleString();
	};

	return (
		<div className={cn('space-y-6 my-20', className)}>
			{/* Header with Back Button */}
			<div>
				<Button
					variant='ghost'
					size='sm'
					onClick={() => navigate('/admin/analytics')}
					className='hover:bg-yellow-500/10'
				>
					<ArrowLeft className='h-4 w-4 mr-2' />
					Back to Analytics
				</Button>
			</div>
			<div className='flex items-center justify-between'>
				<div className='flex items-center gap-4'>
					<div>
						<h1 className='text-3xl font-bold text-white flex items-center gap-3'>
							<Wallet className='h-8 w-8 text-emerald-500' />
							KARMA Social Rewards
						</h1>
						<p className='text-muted-foreground mt-2'>
							Track social tipping, rewards, and KARMA
							distribution
						</p>
					</div>
				</div>
				<Badge
					variant='outline'
					className='bg-gradient-to-r from-emerald-500 to-green-600 text-white border-0 px-4 py-2'
				>
					Real-time Analytics
				</Badge>
			</div>

			{/* Web3 Tipping Analytics */}
			<Card>
				<CardHeader>
					<CardTitle className='flex items-center gap-2'>
						<Wallet className='h-5 w-5' />
						KARMA Social Rewards Overview
					</CardTitle>
				</CardHeader>
				<CardContent>
					{userTipStats ? (
						<div className='space-y-6'>
							{/* User Tip Stats */}
							<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
								<div className='text-center p-4 bg-gradient-to-br from-emerald-500/10 to-green-500/10 border border-emerald-500/20 rounded-lg'>
									<div className='text-2xl font-bold text-emerald-500'>
										{formatNumber(
											userTipStats.totalTipsReceived
										)}
									</div>
									<div className='text-sm text-muted-foreground'>
										Rewards Received
									</div>
								</div>
								<div className='text-center p-4 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20 rounded-lg'>
									<div className='text-2xl font-bold text-blue-500'>
										{parseFloat(
											userTipStats.totalAmountReceived
										).toFixed(2)}
									</div>
									<div className='text-sm text-muted-foreground'>
										KARMA Received
									</div>
								</div>
								<div className='text-center p-4 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg'>
									<div className='text-2xl font-bold text-purple-500'>
										{formatNumber(
											userTipStats.uniqueTippers
										)}
									</div>
									<div className='text-sm text-muted-foreground'>
										Senders
									</div>
								</div>
								<div className='text-center p-4 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-lg'>
									<div className='text-2xl font-bold text-orange-500'>
										{parseFloat(
											userTipStats.totalAmountSent
										).toFixed(2)}
									</div>
									<div className='text-sm text-muted-foreground'>
										KARMA Sent
									</div>
								</div>
							</div>

							{/* Recent Tips Section */}
							{recentTips && recentTips.length > 0 && (
								<div>
									<h3 className='text-lg font-semibold mb-4'>
										Recent Tip Transactions
									</h3>
									<div className='space-y-2'>
										{recentTips
											.slice(0, 5)
											.map((tip: any) => (
												<div
													key={tip._id}
													className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg'
												>
													<div className='flex items-center gap-3'>
														<div className='h-8 w-8 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center'>
															<Wallet className='h-4 w-4 text-white' />
														</div>
														<div>
															<div className='font-medium'>
																{tip.senderName}{' '}
																→{' '}
																{
																	tip.recipientName
																}
															</div>
															<div className='text-sm text-muted-foreground'>
																{format(
																	new Date(
																		tip.timestamp
																	),
																	'MMM d, yyyy h:mm a'
																)}
															</div>
														</div>
													</div>
													<div className='text-right'>
														<div className='font-bold text-emerald-600'>
															{tip.amount} KARMA
														</div>
														{tip.contentHistoryId && (
															<div className='text-xs text-muted-foreground'>
																Post tip
															</div>
														)}
													</div>
												</div>
											))}
									</div>
								</div>
							)}

							{/* Leaderboards */}
							<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
								{/* Top Tip Receivers */}
								{topTipReceivers &&
									topTipReceivers.length > 0 && (
										<div>
											<h3 className='text-lg font-semibold mb-4'>
												Top Tip Receivers
											</h3>
											<div className='space-y-2'>
												{topTipReceivers.map(
													(
														user: any,
														index: number
													) => (
														<div
															key={user.userId}
															className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg'
														>
															<div className='flex items-center gap-3'>
																<div className='h-8 w-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm'>
																	{index + 1}
																</div>
																<div>
																	<div className='font-medium'>
																		{
																			user.userName
																		}
																	</div>
																	<div className='text-sm text-muted-foreground'>
																		{
																			user.totalTips
																		}{' '}
																		tips
																		from{' '}
																		{
																			user.uniqueCounterparts
																		}{' '}
																		users
																	</div>
																</div>
															</div>
															<div className='font-bold text-pink-600'>
																{parseFloat(
																	user.totalAmount
																).toFixed(
																	2
																)}{' '}
																KARMA
															</div>
														</div>
													)
												)}
											</div>
										</div>
									)}

								{/* Top Tip Senders */}
								{topTipSenders && topTipSenders.length > 0 && (
									<div>
										<h3 className='text-lg font-semibold mb-4'>
											Top Tip Senders
										</h3>
										<div className='space-y-2'>
											{topTipSenders.map(
												(user: any, index: number) => (
													<div
														key={user.userId}
														className='flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg'
													>
														<div className='flex items-center gap-3'>
															<div className='h-8 w-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm'>
																{index + 1}
															</div>
															<div>
																<div className='font-medium'>
																	{
																		user.userName
																	}
																</div>
																<div className='text-sm text-muted-foreground'>
																	{
																		user.totalTips
																	}{' '}
																	tips to{' '}
																	{
																		user.uniqueCounterparts
																	}{' '}
																	users
																</div>
															</div>
														</div>
														<div className='font-bold text-blue-600'>
															{parseFloat(
																user.totalAmount
															).toFixed(2)}{' '}
															KARMA
														</div>
													</div>
												)
											)}
										</div>
									</div>
								)}
							</div>
						</div>
					) : (
						<div className='text-center text-muted-foreground py-8'>
							<Wallet className='h-8 w-8 mx-auto mb-2' />
							<div>No tipping data available yet</div>
							<div className='text-sm'>
								Start tipping to see analytics!
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Info Card */}
			<Card className='bg-gradient-to-br from-emerald-500/5 to-green-500/5 border-emerald-500/20'>
				<CardContent className='p-6'>
					<div className='flex items-start gap-4'>
						<div className='p-3 bg-emerald-500/10 rounded-xl'>
							<Wallet className='h-6 w-6 text-emerald-500' />
						</div>
						<div className='flex-1'>
							<h3 className='font-semibold mb-2'>
								About KARMA Social Rewards
							</h3>
							<p className='text-sm text-muted-foreground mb-3'>
								The KARMA Social Rewards system enables users to
								tip each other with KARMA tokens as recognition
								for valuable contributions. Tips are instantly
								transferred on-chain and contribute to user
								reputation scores.
							</p>
							<div className='flex gap-4 text-sm'>
								<div>
									<span className='text-muted-foreground'>
										Min Tip:
									</span>
									<span className='ml-2 font-medium'>
										0.1 KARMA
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Max Tip:
									</span>
									<span className='ml-2 font-medium'>
										100 KARMA
									</span>
								</div>
								<div>
									<span className='text-muted-foreground'>
										Network:
									</span>
									<span className='ml-2 font-medium text-emerald-500'>
										Ethereum
									</span>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
